/**
 * Interlude — gentle "float in on scroll" reveal for the final section.
 *
 * Plain script (no bundler). Loaded with `defer` from the section, which sits
 * earlier in the document than the GSAP libraries at the end of <body>; defer
 * scripts run in document order, so we wait for GSAP to be ready before init.
 *
 * Not pinned, not scrubbed — unlike the Scene. A single ScrollTrigger fires once
 * as the section enters the viewport, floating each text/icon block up and fading
 * it in with a small stagger. Light and one-shot, in keeping with the site's tone.
 *
 * Respects prefers-reduced-motion: no tween — the composed rest state is left as
 * the CSS renders it. Re-initialises cleanly on editor section reloads.
 */
(function () {
  var triggers = []; // ScrollTriggers we create (for editor teardown)
  var tweens = []; // tweens we create

  function ready() {
    return window.gsap && window.ScrollTrigger;
  }

  function whenReady(cb) {
    if (ready()) return cb();
    // Poll until the GSAP libs (later in the document) have executed, so init
    // can never silently no-op if they aren't ready at DOMContentLoaded.
    var tries = 0;
    (function poll() {
      if (ready()) return cb();
      if (tries++ > 600) return; // ~10s safety cap
      requestAnimationFrame(poll);
    })();
  }

  function reset() {
    triggers.forEach(function (t) {
      t.kill();
    });
    tweens.forEach(function (t) {
      t.kill();
    });
    triggers = [];
    tweens = [];
    document.querySelectorAll('[data-interlude]').forEach(function (s) {
      s.__interludeInit = false;
    });
  }

  // gsap.to() throws on a null target — filter optional content out.
  function present(list) {
    return list.filter(function (el) {
      return !!el;
    });
  }

  function setup(section) {
    if (!section || section.__interludeInit) return;
    section.__interludeInit = true;

    var items = present([].slice.call(section.querySelectorAll('[data-il-float]')));
    if (!items.length) return;

    // "Static mode": no float-in tween. True under reduced-motion AND inside
    // the Shopify theme editor (Shopify.designMode), so editing shows the
    // composed rest state the CSS already renders.
    var staticMode =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !!(window.Shopify && window.Shopify.designMode);
    if (staticMode) return;

    // Reveal in three timed groups — heading first, then the icon row, then the
    // bottom (body + button) — each with a little internal stagger and a short
    // offset between groups, so the composition arrives top-to-bottom.
    var groups = [
      present([
        section.querySelector('.interlude__heading'),
        section.querySelector('.interlude__tag')
      ]),
      present([].slice.call(section.querySelectorAll('.interlude__row [data-il-float]'))),
      present([
        section.querySelector('.interlude__body'),
        section.querySelector('.interlude__btn')
      ])
    ];

    gsap.set(items, { y: 36, autoAlpha: 0, filter: 'blur(6px)' });

    // Trigger off the vertically-centred content, not the tall (100svh) section:
    // triggering on the section top fires while the centred content is still
    // below the fold, so the one-shot tween finishes before it's ever seen.
    var tl = gsap.timeline({
      scrollTrigger: {
        trigger: section.querySelector('.interlude__inner') || section,
        start: 'top 85%',
        once: true,
        // The Interlude sits after the pinned Scene/intro. Without a low refresh
        // priority ScrollTrigger measures this start against pre-pin geometry, so
        // 'top 85%' maps to the wrong scroll point and (being `once`) fires in a
        // dead zone before it's ever on screen. Matches survey.js initReveal.
        invalidateOnRefresh: true,
        refreshPriority: -1
      }
    });

    var pos = 0;
    groups.forEach(function (grp) {
      if (!grp.length) return;
      tl.to(
        grp,
        {
          y: 0,
          autoAlpha: 1,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: 'power3.out',
          stagger: 0.1
        },
        pos
      );
      pos += 0.35; // small gap before the next group starts
    });

    tweens.push(tl);
    if (tl.scrollTrigger) triggers.push(tl.scrollTrigger);
  }

  function initAll() {
    gsap.registerPlugin(ScrollTrigger);
    document.querySelectorAll('[data-interlude]').forEach(setup);
  }

  whenReady(initAll);

  // Recalculate once images/fonts have settled.
  window.addEventListener('load', function () {
    if (window.ScrollTrigger) ScrollTrigger.refresh();
  });

  // Shopify theme editor: tear down + rebuild when the section reloads, so
  // changing a setting never leaves a block stuck at its hidden state.
  document.addEventListener('shopify:section:load', function (e) {
    if (!ready()) return;
    if (!e.target.querySelector('[data-interlude]')) return;
    reset();
    document.querySelectorAll('[data-interlude]').forEach(setup);
  });
})();
